/**
 * ImageCarousel Component
 * Cycles through multiple images automatically
 * Used in card views where drops have multiple card_images
 */
import React, { useState, useEffect, useCallback } from "react";
import { getMediaUrl } from "../../utils";

const ImageCarousel = ({ 
  images = [], 
  fallbackImage = null,
  interval = 3000, 
  className = "",
  alt = "Image",
  showIndicators = true,
  pauseOnHover = true
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  // Get valid images array - route through CDN
  const validImages = images.filter(img => img && img.trim() !== '').map(img => getMediaUrl(img));
  const allImages = validImages.length > 0 ? validImages : (fallbackImage ? [getMediaUrl(fallbackImage)] : []);

  // Cycle through images
  useEffect(() => {
    if (allImages.length <= 1 || isPaused) return;

    const timer = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % allImages.length);
    }, interval);

    return () => clearInterval(timer);
  }, [allImages.length, interval, isPaused]);

  const handleMouseEnter = useCallback(() => {
    if (pauseOnHover) setIsPaused(true);
  }, [pauseOnHover]);

  const handleMouseLeave = useCallback(() => {
    if (pauseOnHover) setIsPaused(false);
  }, [pauseOnHover]);

  if (allImages.length === 0) {
    return (
      <div className={`bg-gray-800 flex items-center justify-center ${className}`}>
        <span className="text-amber-400 font-black text-xl">BLK</span>
      </div>
    );
  }

  return (
    <div 
      className={`relative overflow-hidden ${className}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Image */}
      <img 
        src={allImages[currentIndex]} 
        alt={`${alt} ${currentIndex + 1}`}
        className="w-full h-full object-cover transition-opacity duration-500"
      />
      
      {/* Indicators - only show if multiple images */}
      {showIndicators && allImages.length > 1 && (
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
          {allImages.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentIndex(idx)}
              className={`w-2 h-2 rounded-full transition-all ${
                idx === currentIndex 
                  ? 'bg-orange-500 w-4' 
                  : 'bg-white/40 hover:bg-white/60'
              }`}
              aria-label={`Go to image ${idx + 1}`}
            />
          ))}
        </div>
      )}
      
      {/* Card count badge - only show if multiple images */}
      {allImages.length > 1 && (
        <div className="absolute top-2 right-2 bg-[#12121e]/70 backdrop-blur-sm px-2 py-1 rounded-full text-[10px] text-white font-medium">
          {currentIndex + 1}/{allImages.length}
        </div>
      )}
    </div>
  );
};

export default ImageCarousel;
